import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ApiService } from 'src/app/services';
import { MatIconModule } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { DocumentType } from 'src/app/models/document_type.model';
import { Document } from 'src/app/models/document.model';
import { DocumentRelatedType, DocumentRelatedTypeLabels } from 'src/app/enums/document-related-type.enum';
import { DocumentRelatedEntity } from 'src/app/models/document-related-entity.model';
import { dateAfterStartValidator } from 'src/app/validators/date-after-start.validator';
import { fileSizeValidator } from 'src/app/validators/file-size.validator';

@Component({
  selector: 'app-create-document-dialog',
  standalone: true,
  templateUrl: './create-document-dialog.component.html',
  styleUrl: './create-document-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    CdkScrollable,
  ],
})
export class CreateDocumentDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  dialogRef = inject<MatDialogRef<CreateDocumentDialogComponent>>(MatDialogRef);
  data = inject<CreateDocumentDialogData>(MAT_DIALOG_DATA);

  form: FormGroup;
  relatedEntities: DocumentRelatedEntity[] = [];
  selectedType: DocumentType | null = null;
  DocumentRelatedType = DocumentRelatedType;
  relatedTypeLabel: string = '';
  maxUploadSizeMB: number = 10;

  constructor() {
    this.form = this.fb.group({
      comment: ['', Validators.maxLength(255)],
      document_type_id: ['', Validators.required],
      start_date: ['', Validators.required],
      end_date: ['', dateAfterStartValidator('start_date')],
      file: ['', [Validators.required, fileSizeValidator(this.maxUploadSizeMB)]],
      related_type: ['', [Validators.required, Validators.maxLength(255)]],
      related_id: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.api.getMaxUploadSize().subscribe({
      next: (maxSizeKB: number) => {
        this.maxUploadSizeMB = Math.round(maxSizeKB / 1024);
        this.form.get('file')?.setValidators([Validators.required, fileSizeValidator(this.maxUploadSizeMB)]);
        this.form.get('file')?.updateValueAndValidity();
      },
    });

    this.form.get('document_type_id')?.valueChanges.subscribe((typeId: string): void => {
      this.selectedType = this.data.types.find((t: DocumentType): boolean => t.id === Number(typeId)) ?? null;

      this.relatedTypeLabel = this.getRelatedTypeLabel(this.selectedType?.related_type);

      if (this.selectedType?.related_type === DocumentRelatedType.User) {
        this.form.patchValue({ related_id: this.data.userId });
        this.relatedEntities = [];
      } else if (this.selectedType?.related_type) {
        this.form.patchValue({ related_id: null });
        this.getRelatedModels(this.selectedType.related_type, this.data.userId);
      } else {
        this.form.patchValue({ related_id: null });
        this.relatedEntities = [];
      }
    });

    this.form.get('related_id')?.valueChanges.subscribe((id: string | number): void => {
      if (id && this.selectedType?.related_type) {
        this.form.patchValue({ related_type: this.selectedType.related_type });
      } else {
        this.form.patchValue({ related_type: null });
      }
    });

    this.form.get('start_date')?.valueChanges.subscribe(() => {
      this.form.get('end_date')?.updateValueAndValidity();
    });
  }

  save(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.valid) {
      const formData = new FormData();
      Object.entries(this.form.value).forEach(([key, value]) => {
        if (value instanceof Blob) {
          formData.append(key, value);
        } else if (value instanceof Date) {
          const yyyy = value.getFullYear();
          const mm = String(value.getMonth() + 1).padStart(2, '0');
          const dd = String(value.getDate()).padStart(2, '0');
          formData.append(key, `${yyyy}-${mm}-${dd}`);
        } else if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      this.api.createDocument(formData).subscribe({
        next: (res: Document): void => {
          this.dialogRef.close(res);
        },
      });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      this.form.patchValue({ file });
      this.form.get('file')?.updateValueAndValidity();
    }
  }

  getFileSize(file: File): string {
    if (!file) return '';

    const bytes = file.size;
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getRelatedModels(relatedType: string, userId: string): void {
    this.api.getRelatedModels(relatedType, Number(userId)).subscribe({
      next: (res: DocumentRelatedEntity[]): void => {
        this.relatedEntities = res.map((entity, index) => ({
          ...entity,
          trackBy: `${relatedType}-${entity.id}-${entity.start_date || ''}-${entity.end_date || ''}-${index}`,
        }));
      },
    });
  }

  private getRelatedTypeLabel(relatedType: string | undefined): string {
    if (!relatedType) return '';
    return DocumentRelatedTypeLabels[relatedType as DocumentRelatedType] || relatedType;
  }
}

interface CreateDocumentDialogData {
  types: DocumentType[];
  userId: string;
}
